import { useCallback, useRef, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Select, Tag, message } from 'antd';
import {
  EyeOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getSocialMessages,
  markMessageRead,
  type SocialMessage,
  type SocialMessageFilters,
} from '@/api/socialApi';
import { useAuth } from '@/hooks/useAuth';

const PLATFORM_COLORS: Record<string, string> = {
  facebook: 'blue',
  instagram: 'magenta',
  twitter: 'cyan',
  linkedin: 'geekblue',
  wechat: 'green',
};

export default function SocialMessageListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [filters, setFilters] = useState<SocialMessageFilters>({});

  const canWrite = hasPermission('social_messages', 'write');

  const handleFilterChange = useCallback(
    (key: keyof SocialMessageFilters, value: string | undefined) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const fetchData = useCallback(
    (params: SearchParams) => getSocialMessages({ ...params, ...filters }),
    [filters]
  );

  const filterMounted = useRef(false);
  useEffect(() => {
    if (!filterMounted.current) {
      filterMounted.current = true;
      return;
    }
    gridRef.current?.reload();
  }, [filters]);

  const handleMarkRead = useCallback(
    async (id: string) => {
      try {
        await markMessageRead(id);
        message.success(t('social.action.mark_read_success'));
        gridRef.current?.reload();
      } catch {
        message.error(t('app.error'));
      }
    },
    [t]
  );

  const columns: ColumnsType<SocialMessage> = useMemo(
    () => [
      {
        title: t('social.field.content'),
        dataIndex: 'content',
        key: 'content',
        ellipsis: true,
        render: (content: string, record: SocialMessage) => (
          <a onClick={() => navigate(`/social/messages/${record.id}`)}>
            {content.substring(0, 80)}{content.length > 80 ? '...' : ''}
          </a>
        ),
      },
      {
        title: t('social.field.platform'),
        dataIndex: 'platform',
        key: 'platform',
        width: 110,
        render: (platform: string) => (
          <Tag color={PLATFORM_COLORS[platform] ?? 'default'}>
            {t(`social.platform.${platform}`, platform)}
          </Tag>
        ),
      },
      {
        title: t('social.field.direction'),
        dataIndex: 'direction',
        key: 'direction',
        width: 100,
        render: (dir: string) => (
          <Tag color={dir === 'inbound' ? 'blue' : 'green'}>
            {t(`social.direction.${dir}`, dir)}
          </Tag>
        ),
      },
      {
        title: t('social.field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 90,
        render: (status: string) => (
          <Tag color={status === 'unread' ? 'orange' : 'default'}>
            {t(`social.status.${status}`, status)}
          </Tag>
        ),
      },
      {
        title: t('social.field.contact_id'),
        dataIndex: 'contact_id',
        key: 'contact_id',
        ellipsis: true,
        width: 120,
      },
      {
        title: t('social.field.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        width: 170,
        render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 120,
        render: (_: unknown, record: SocialMessage) => (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/social/messages/${record.id}`)}
            />
            {canWrite && record.status === 'unread' && (
              <Button
                type="link"
                size="small"
                icon={<CheckOutlined />}
                onClick={() => handleMarkRead(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, navigate, canWrite, handleMarkRead]
  );

  const platformOptions = useMemo(
    () => [
      { value: '', label: t('social.filter.all') },
      { value: 'facebook', label: t('social.platform.facebook') },
      { value: 'instagram', label: t('social.platform.instagram') },
      { value: 'twitter', label: t('social.platform.twitter') },
      { value: 'linkedin', label: t('social.platform.linkedin') },
      { value: 'wechat', label: t('social.platform.wechat') },
    ],
    [t]
  );

  const directionOptions = useMemo(
    () => [
      { value: '', label: t('social.filter.all') },
      { value: 'inbound', label: t('social.direction.inbound') },
      { value: 'outbound', label: t('social.direction.outbound') },
    ],
    [t]
  );

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('social.filter.all') },
      { value: 'read', label: t('social.status.read') },
      { value: 'unread', label: t('social.status.unread') },
    ],
    [t]
  );

  const filterBar = (
    <Space wrap>
      <Select
        placeholder={t('social.filter.platform')}
        style={{ width: 140 }}
        allowClear
        options={platformOptions}
        onChange={(val) => handleFilterChange('platform', val || undefined)}
      />
      <Select
        placeholder={t('social.filter.direction')}
        style={{ width: 120 }}
        allowClear
        options={directionOptions}
        onChange={(val) => handleFilterChange('direction', val || undefined)}
      />
      <Select
        placeholder={t('social.filter.status')}
        style={{ width: 120 }}
        allowClear
        options={statusOptions}
        onChange={(val) => handleFilterChange('status', val || undefined)}
      />
    </Space>
  );

  return (
    <div>
      <h2>{t('social.messages_title')}</h2>
      <DataGrid<SocialMessage>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('social.search_messages_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={filterBar}
        onRow={(record) => ({
          onDoubleClick: () => navigate(`/social/messages/${record.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
}
